import React from 'react';
import { Grid, Column, Tag, InlineNotification } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  Zap,
  Cloud,
  Activity,
  BarChart3
} from 'lucide-react';
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

// Import existing components (unchanged)
import PurchasedEmissionsNotification from '../PurchasedEmissionsNotification/PurchasedEmissionsNotification';
import ValidationSummary from '../ValidationSummary/ValidationSummary';

// Keep all the existing type definitions and helper functions
type StatusType = 'warning' | 'success';

const isFiniteNumber = (v: any): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const eitherOrStatus = ({
  defaultDiff,
  countryDiff,
  thresholdDefault,
  thresholdCountry,
}: {
  defaultDiff: number | undefined;
  countryDiff: number | undefined;
  thresholdDefault: number;
  thresholdCountry: number;
}) => {
  const withinDefault =
    isFiniteNumber(defaultDiff) && Math.abs(defaultDiff) <= thresholdDefault;
  const withinCountry =
    isFiniteNumber(countryDiff) && Math.abs(countryDiff) <= thresholdCountry;

  if (withinDefault || withinCountry) {
    if (withinDefault) {
      return { status: 'success' as StatusType, difference: defaultDiff!, via: 'default' as const };
    }
    return { status: 'success' as StatusType, difference: countryDiff!, via: 'country' as const };
  }
  const difference = isFiniteNumber(countryDiff)
    ? countryDiff!
    : isFiniteNumber(defaultDiff)
    ? defaultDiff!
    : 0;
  return { status: 'warning' as StatusType, difference, via: 'none' as const };
};

// Custom components for the redesign
const EmissionCard = ({ 
  title, 
  value, 
  unit, 
  icon: Icon, 
  status, 
  trend 
}: {
  title: string;
  value: string;
  unit: string;
  icon: React.ComponentType<{ size?: number | string; style?: React.CSSProperties }>;
  status?: 'success' | 'warning';
  trend?: number;
}) => {
  const bgColor = status === 'success' ? '#d4f4dd' : status === 'warning' ? '#fff3cd' : '#f4f4f4';
  const borderColor = status === 'success' ? '#42be65' : status === 'warning' ? '#f1c21b' : '#e0e0e0';
  const trendColor = trend && trend > 0 ? '#da1e28' : '#0f62fe';
  
  return (
    <div style={{
      background: bgColor,
      border: `2px solid ${borderColor}`,
      borderRadius: '12px',
      padding: '24px',
      position: 'relative',
      transition: 'all 0.3s ease',
      height: '100%'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <Icon size={24} style={{ color: borderColor }} />
        {trend !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: trendColor, fontSize: '14px' }}>
            {trend > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            <span>{Math.abs(trend).toFixed(1)}%</span>
          </div>
        )}
      </div>
      <div style={{ fontSize: '14px', color: '#525252', marginBottom: '8px' }}>{title}</div>
      <div style={{ fontSize: '32px', fontWeight: '600', color: '#161616' }}>
        {value}
        <span style={{ fontSize: '16px', fontWeight: '400', marginLeft: '8px', color: '#525252' }}>{unit}</span>
      </div>
    </div>
  );
};

const ComparisonGauge = ({
  label,
  value,
  reference,
  threshold,
  status,
}: {
  label: string;
  value: number;
  reference: number;
  threshold: number;
  status: StatusType | undefined;
}) => {
  // raw absolute % difference (can exceed 100)
  const pctRaw =
    reference === 0 ? 0 : Math.abs(((value - reference) / reference) * 100);

  // pick a nice upper bound so the arc can grow when >100%
  // (snap to the next 20% step, cap at 300% to avoid silly scales)
  const domainMax = Math.min(
    300,
    Math.max(100, Math.ceil(pctRaw / 20) * 20)
  );

  const arcColor = status === 'success' ? 'var(--color-success)' : 'var(--color-warning)';
  const data = [{ value: Math.min(pctRaw, domainMax), fill: arcColor }];

  return (
    <div style={{ textAlign: 'center' }}>
      <ResponsiveContainer width="100%" height={120}>
        <RadialBarChart
          cx="50%"
          cy="50%"
          innerRadius="60%"
          outerRadius="90%"
          startAngle={180}
          endAngle={0}
          data={data}
        >
          <PolarAngleAxis
            type="number"
            domain={[0, domainMax]}
            angleAxisId={0}
            tick={false}
          />
          <RadialBar dataKey="value" cornerRadius={10} fill={arcColor} />
        </RadialBarChart>
      </ResponsiveContainer>

      {/* Label shows the true % (even if > domainMax) */}
      <div style={{ marginTop: '-40px' }}>
        <div style={{ fontSize: 24, fontWeight: 600 }}>
          {pctRaw.toFixed(1)}%
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-subtext)' }}>
          {label} · scale 0–{domainMax}%
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-subtext)', marginTop: 4 }}>
          accepted band ±{threshold}%
        </div>
      </div>
    </div>
  );
};

const StatusIndicator = ({ status, message }: { status: 'success' | 'warning'; message: string }) => {
  const Icon = status === 'success' ? CheckCircle : AlertTriangle;
  const color = status === 'success' ? '#42be65' : '#f1c21b';
  
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px',
      padding: '12px 16px',
      background: status === 'success' ? '#d4f4dd' : '#fff3cd',
      borderRadius: '8px',
      marginBottom: '8px'
    }}>
      <Icon size={20} style={{ color, flexShrink: 0 }} />
      <span style={{ fontSize: '14px', color: '#161616' }}>{message}</span>
    </div>
  );
};

interface EmissionsComparisonProps {
  defaultEmissions?: { direct?: number; indirect?: number } | null;
  emissionIntensity?: { direct_emissions?: number; indirect_emissions?: number } | null;
  calculatedEmissionsDirectPerTonne?: number;
  calculatedEmissionsIndirectPerTonne?: number;
  hasRenewableEnergy?: boolean;
  renewablePercentage?: number;
  formState?: any;
  className?: string;
  comparisonStatus?: any;
}

const EmissionsComparison: React.FC<EmissionsComparisonProps> = (props) => {
  const {
    defaultEmissions,
    emissionIntensity,
    calculatedEmissionsDirectPerTonne,
    calculatedEmissionsIndirectPerTonne,
    hasRenewableEnergy = false,
    renewablePercentage = 0,
    formState = {},
    className,
    comparisonStatus,
  } = props;

  const { t } = useTranslation();

  // after: const { t } = useTranslation();
const legendNames = React.useMemo(
  () => ({
    yours: t('overview.yours', 'Your Values'),
    euDefault: t('overview.eudefault', 'EU Default'),
    country: t('overview.country', 'Country'),
  }),
  [t]
);


  // Keep all the existing calculation logic exactly as is
  const directVal = React.useMemo(() => {
    const v =
      calculatedEmissionsDirectPerTonne ??
      formState?.calculatedEmissionsDirectPerTonne;
    return typeof v === 'number' ? v : Number(v ?? 0);
  }, [calculatedEmissionsDirectPerTonne, formState?.calculatedEmissionsDirectPerTonne]);

  const indirectVal = React.useMemo(() => {
    const v =
      calculatedEmissionsIndirectPerTonne ??
      formState?.calculatedEmissionsIndirectPerTonne;
    return typeof v === 'number' ? v : Number(v ?? 0);
  }, [calculatedEmissionsIndirectPerTonne, formState?.calculatedEmissionsIndirectPerTonne]);

  const getIndirectEmissionThreshold = React.useCallback(() => {
    let threshold = 20;
    if (renewablePercentage >= 99.9) threshold = 100;
    else if (renewablePercentage >= 75) threshold = 80;
    else if (renewablePercentage >= 50) threshold = 60;
    else if (renewablePercentage >= 25) threshold = 40;
    else if (renewablePercentage > 0) threshold = 30;
    return threshold;
  }, [renewablePercentage]);

  const indirectThreshold = React.useMemo(
    () => (hasRenewableEnergy ? getIndirectEmissionThreshold() : 20),
    [hasRenewableEnergy, getIndirectEmissionThreshold]
  );

  // Keep all diff calculations
  const directDiffDefault = React.useMemo(() => {
    const ref = defaultEmissions?.direct;
    if (!isFiniteNumber(ref)) return undefined;
    if (ref === 0) return directVal > 0 ? 100 : 0;
    return ((directVal - ref) / ref) * 100;
  }, [defaultEmissions?.direct, directVal]);

  const indirectDiffDefault = React.useMemo(() => {
    const ref = defaultEmissions?.indirect;
    if (!isFiniteNumber(ref)) return undefined;
    if (Math.abs(indirectVal) < 0.001 && ref > 0) return -100;
    if (ref === 0) return indirectVal > 0 ? 100 : 0;
    return ((indirectVal - ref) / ref) * 100;
  }, [defaultEmissions?.indirect, indirectVal]);

  const directDiffCountry = React.useMemo(() => {
    const ref = emissionIntensity?.direct_emissions;
    if (!isFiniteNumber(ref)) return undefined;
    if (ref === 0) return 100;
    return ((directVal - ref) / ref) * 100;
  }, [emissionIntensity?.direct_emissions, directVal]);

  const indirectDiffCountry = React.useMemo(() => {
    const ref = emissionIntensity?.indirect_emissions;
    if (!isFiniteNumber(ref)) return undefined;
    if (ref === 0) return 100;
    return ((indirectVal - ref) / ref) * 100;
  }, [emissionIntensity?.indirect_emissions, indirectVal]);

  // Status calculations
  const directStatusDefault: StatusType | undefined =
    isFiniteNumber(directDiffDefault)
      ? Math.abs(directDiffDefault) <= 20 ? 'success' : 'warning'
      : undefined;

  const indirectStatusDefault: StatusType | undefined =
    isFiniteNumber(indirectDiffDefault)
      ? Math.abs(indirectDiffDefault) <= indirectThreshold ? 'success' : 'warning'
      : undefined;

  const directStatusCountry: StatusType | undefined =
    isFiniteNumber(directDiffCountry)
      ? Math.abs(directDiffCountry) <= 20 ? 'success' : 'warning'
      : undefined;

  const indirectStatusCountry: StatusType | undefined =
    isFiniteNumber(indirectDiffCountry)
      ? Math.abs(indirectDiffCountry) <= indirectThreshold ? 'success' : 'warning'
      : undefined;

  const directCombined = React.useMemo(
    () =>
      eitherOrStatus({
        defaultDiff: directDiffDefault,
        countryDiff: directDiffCountry,
        thresholdDefault: 20,
        thresholdCountry: 20,
      }),
    [directDiffDefault, directDiffCountry]
  );

  const indirectCombined = React.useMemo(
    () =>
      eitherOrStatus({
        defaultDiff: indirectDiffDefault,
        countryDiff: indirectDiffCountry,
        thresholdDefault: indirectThreshold,
        thresholdCountry: indirectThreshold,
      }),
    [indirectDiffDefault, indirectDiffCountry, indirectThreshold]
  );

  // Activity calculations
  const prod = Number(formState?.productionCalc ?? 0);
  const activityFromFields =
    Number(formState?.activityTotal ?? formState?.totalActivity ?? formState?.activity ?? 0);
  const activityFromDirect =
    Array.isArray(formState?.directEmissions)
      ? formState.directEmissions.reduce(
          (sum: number, e: any) => sum + Number(e?.activity ?? e?.weight ?? 0),
          0
        )
      : 0;
  const activityValue = activityFromFields || activityFromDirect || 0;
  const activityDeltaT = activityValue - prod;
  const activityDeltaPct = prod === 0 ? 0 : ((activityValue - prod) / prod) * 100;
  const activityStatus: 'success' | 'warning' =
    activityDeltaPct < -20 || activityDeltaPct > 50 ? 'warning' : 'success';

const comparisonData = [
  {
    key: 'direct',
    label: t('survey.submission.validation.directEmissionLabel', 'Direct Emissions'),
    yours: directVal,
    euDefault: defaultEmissions?.direct ?? null,
    country: emissionIntensity?.direct_emissions ?? null,
  },
  {
    key: 'indirect',
    label: t('survey.submission.validation.indirectEmissionLabel', 'Indirect Emissions'),
    yours: indirectVal,
    euDefault: defaultEmissions?.indirect ?? null,
    country: emissionIntensity?.indirect_emissions ?? null,
  },
];



  const overallStatus = directCombined.status === 'success' && indirectCombined.status === 'success';

  return (
    <div className={className || 'emissions-comparison'} style={{ padding: '24px' }}>
      <Grid>
        {/* Hero Section - Your Emissions */}
        <Column sm={4} md={8} lg={16}>
<div className="heroGradient">
            <h2 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '24px' }}>
              {t('survey.submission.validation.userEmissionValues', 'Your Calculated Emissions')}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
              <EmissionCard
                title={t('survey.submission.validation.directEmissionLabel', 'Direct Emissions')}
                value={directVal.toFixed(2)}
                unit="t CO2e/t"
                icon={Zap}
                status={directCombined.status}
                trend={directCombined.difference}
              />
              <EmissionCard
                title={t('survey.submission.validation.indirectEmissionLabel', 'Indirect Emissions')}
                value={indirectVal.toFixed(2)}
                unit="t CO2e/t"
                icon={Cloud}
                status={indirectCombined.status}
                trend={indirectCombined.difference}
              />
            </div>
          </div>
        </Column>

        {/* Quick Status */}
        <Column sm={4} md={8} lg={16}>
          <div style={{ 
            background: overallStatus ? '#d4f4dd' : '#fff3cd',
            border: `2px solid ${overallStatus ? '#42be65' : '#f1c21b'}`,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {overallStatus ? (
                <CheckCircle size={32} style={{ color: '#42be65' }} />
              ) : (
                <AlertTriangle size={32} style={{ color: '#f1c21b' }} />
              )}
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '4px' }}>
                  {overallStatus ? 'Emissions Within Acceptable Range' : 'Review Required'}
                </h3>
                <p style={{ fontSize: '14px', color: '#525252', margin: 0 }}>
                  {overallStatus 
                    ? 'Your emissions are within the acceptable thresholds for validation.'
                    : 'Some emissions values exceed the validation thresholds. Please review the details below.'}
                </p>
              </div>
            </div>
          </div>
        </Column>

        {/* Purchased Emissions Notification */}
        <Column sm={4} md={8} lg={16}>
          <PurchasedEmissionsNotification purchasedEmissions={formState.purchasedEmissions} />
        </Column>

        {/* Visual Comparison */}
        <Column sm={4} md={8} lg={16}>
          <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '24px', marginBottom: '32px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px' }}>
              Emissions Comparison Overview
            </h3>
         <ResponsiveContainer width="100%" height={300}>
  <BarChart data={comparisonData} barCategoryGap="28%" barGap={6}>
    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-grid)" />
    <XAxis
      dataKey="label"
      type="category"
      allowDuplicatedCategory={false}
      axisLine={false}
      tickLine={false}
      interval={0}
      tick={{ fill: 'var(--color-subtext)', fontSize: 12 }}
    />
    <YAxis
      type="number"
      domain={[0, 'auto']}
      tick={{ fill: 'var(--color-subtext)', fontSize: 12 }}
      label={{ value: 't CO2e/t', angle: -90, position: 'insideLeft', fill: 'var(--color-subtext)' }}
    />
    <Tooltip
      cursor={{ fill: 'rgba(0,0,0,.04)' }}
      labelFormatter={(label: any, payload: any) =>
        payload?.[0]?.payload?.label ?? label
      }
      formatter={(value: any, key?: string) => [
        Number.isFinite(value) ? Number(value).toFixed(2) : '—',
        (key && legendNames[key as keyof typeof legendNames]) ?? key ?? '',
      ]}
    />
    <Bar dataKey="yours"     name={legendNames.yours}     fill="var(--color-yours)" />
    <Bar dataKey="euDefault" name={legendNames.euDefault} fill="var(--color-eudefault)" />
    {(emissionIntensity?.direct_emissions ?? emissionIntensity?.indirect_emissions) != null && (
      <Bar dataKey="country" name={legendNames.country}   fill="var(--color-country)" />
    )}
  </BarChart>
</ResponsiveContainer>


          </div>
        </Column>

        {/* Detailed Comparisons Grid */}
        <Column sm={4} md={4} lg={8}>
          <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '24px', marginBottom: '32px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px' }}>
              EU Default Comparison
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <ComparisonGauge
                label="Direct vs Default"
                value={directVal}
                reference={defaultEmissions?.direct || 0}
                threshold={20}
                status={directStatusDefault}
              />
              <ComparisonGauge
                label="Indirect vs Default"
                value={indirectVal}
                reference={defaultEmissions?.indirect || 0}
                threshold={indirectThreshold}
                status={indirectStatusDefault}
              />
            </div>
            <div style={{ marginTop: '16px' }}>
              {directStatusDefault === 'success' ? (
                <StatusIndicator status="success" message={`Direct emissions within ±20% of EU default`} />
              ) : (
                <StatusIndicator status="warning" message={`Direct emissions exceed ±20% threshold`} />
              )}
              {indirectStatusDefault === 'success' ? (
                <StatusIndicator status="success" message={`Indirect emissions within ±${indirectThreshold}% of EU default`} />
              ) : (
                <StatusIndicator status="warning" message={`Indirect emissions exceed ±${indirectThreshold}% threshold`} />
              )}
            </div>
          </div>
        </Column>

        {/* Country Comparison if available */}
        {(emissionIntensity?.direct_emissions || emissionIntensity?.indirect_emissions) && (
          <Column sm={4} md={4} lg={8}>
            <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '24px', marginBottom: '32px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px' }}>
                Country-Specific Comparison
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <ComparisonGauge
                  label="Direct vs Country"
                  value={directVal}
                  reference={emissionIntensity?.direct_emissions || 0}
                  threshold={20}
                  status={directStatusCountry}
                />
                <ComparisonGauge
                  label="Indirect vs Country"
                  value={indirectVal}
                  reference={emissionIntensity?.indirect_emissions || 0}
                  threshold={indirectThreshold}
                  status={indirectStatusCountry}
                />
              </div>
              <div style={{ marginTop: '16px' }}>
                {directStatusCountry === 'success' ? (
                  <StatusIndicator status="success" message={`Direct emissions within ±20% of country average`} />
                ) : (
                  <StatusIndicator status="warning" message={`Direct emissions exceed ±20% threshold`} />
                )}
                {indirectStatusCountry === 'success' ? (
                  <StatusIndicator status="success" message={`Indirect emissions within ±${indirectThreshold}% of country average`} />
                ) : (
                  <StatusIndicator status="warning" message={`Indirect emissions exceed ±${indirectThreshold}% threshold`} />
                )}
              </div>
            </div>
          </Column>
        )}

        {/* Activity vs Production */}
        <Column sm={4} md={8} lg={16}>
          <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '24px', marginBottom: '32px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Activity size={20} />
              {t('survey.submission.validation.activityComparisonTitle', 'Activity vs Production')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
              <div style={{ 
                padding: '16px',
                background: '#f4f4f4',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '14px', color: '#525252', marginBottom: '8px' }}>
                  {t('survey.submission.validation.activityReportedLabel', 'Reported Activity')}
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600' }}>{activityValue.toFixed(2)} t</div>
              </div>
              <div style={{ 
                padding: '16px',
                background: '#f4f4f4',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '14px', color: '#525252', marginBottom: '8px' }}>
                  {t('survey.submission.validation.activityProductionLabel', 'Production Baseline')}
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600' }}>{prod.toFixed(2)} t</div>
              </div>
              <div style={{ 
                padding: '16px',
                background: activityStatus === 'success' ? '#d4f4dd' : '#fff3cd',
                border: `2px solid ${activityStatus === 'success' ? '#42be65' : '#f1c21b'}`,
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '14px', color: '#525252', marginBottom: '8px' }}>
                  {t('survey.submission.validation.activityDeltaLabel', 'Delta')}
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600' }}>
                  {activityDeltaT > 0 ? '+' : ''}{activityDeltaT.toFixed(2)} t
                  <span style={{ fontSize: '14px', marginLeft: '8px', color: activityStatus === 'success' ? '#42be65' : '#f1c21b' }}>
                    ({activityDeltaPct > 0 ? '+' : ''}{activityDeltaPct.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Column>

        {/* Validation Summary */}
        <Column sm={4} md={8} lg={16}>
          <ValidationSummary
            comparisonStatus={comparisonStatus || {
              direct: {
                status: directCombined.status,
                difference: isFiniteNumber(directCombined.difference) ? directCombined.difference : 0,
                via: directCombined.via,
              },
              indirect: {
                status: indirectCombined.status,
                difference: isFiniteNumber(indirectCombined.difference) ? indirectCombined.difference : 0,
                via: indirectCombined.via,
              },
              directIntensity: {
                status: directCombined.status,
                difference: isFiniteNumber(directCombined.difference) ? directCombined.difference : 0,
                via: directCombined.via,
              },
              indirectIntensity: {
                status: indirectCombined.status,
                difference: isFiniteNumber(indirectCombined.difference) ? indirectCombined.difference : 0,
                via: indirectCombined.via,
              },
            }}
            hasRenewableEnergy={hasRenewableEnergy}
            renewablePercentage={renewablePercentage}
            purchasedEmissions={formState.purchasedEmissions}
            surveyStatus={formState.status}
          />
        </Column>
      </Grid>
    </div>
  );
};

export default EmissionsComparison;